import { useState, type ReactNode } from 'react'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { useAdminStats } from '@/entities/admin'
import type { AdminDailyPoint, AdminPeriod } from '@/shared/api/types'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { ElevenLabsCost } from '../shared/ElevenLabsCost'
import { formatApproxUsd, formatUsd, readAdminCosts } from '../shared/adminCosts'
import {
  formatAdminDate,
  formatDay,
  formatDecimal,
  formatDurationSeconds,
  formatNumber,
  formatPercent,
} from '../shared/format'
import { AdminBalanceWidget } from './AdminBalanceWidget'
import common from '../AdminPages.module.css'
import styles from './AdminSummaryPage.module.css'

const PERIODS: { value: AdminPeriod; label: string }[] = [
  { value: 'today', label: 'Сегодня' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: 'all', label: 'Всё время' },
]

export function AdminSummaryPage() {
  const [period, setPeriod] = useState<AdminPeriod>('30d')
  const stats = useAdminStats(period)

  return (
    <>
      <div className={common.headerRow}>
        <PageHeader
          title="Сводка"
          description="Главные показатели по всем компаниям и звонкам."
        />
        <div className={styles.periods} aria-label="Период сводки">
          {PERIODS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`${styles.periodButton} ${period === item.value ? styles.periodActive : ''}`}
              onClick={() => setPeriod(item.value)}
              aria-pressed={period === item.value}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {stats.isPending ? (
        <AdminRemoteState kind="loading" />
      ) : stats.isError || !stats.data ? (
        <AdminRemoteState
          kind="error"
          message="Сводка не загрузилась. Проверьте соединение и повторите запрос."
          onRetry={() => void stats.refetch()}
        />
      ) : (
        <Summary data={stats.data} />
      )}
    </>
  )
}

function Summary({ data }: { data: NonNullable<ReturnType<typeof useAdminStats>['data']> }) {
  const costs = readAdminCosts(data.costs, data)

  return (
    <>
      <p className={styles.range}>
        Период: {formatAdminDate(data.from)} — {formatAdminDate(data.to)}
      </p>

      <AdminBalanceWidget />

      <section aria-labelledby="companies-title">
        <h2 id="companies-title" className={styles.sectionTitle}>
          Компании
        </h2>
        <div className={common.metrics}>
          <Metric
            label="Всего компаний"
            value={formatNumber(data.companies.total)}
            hint={`Новых за 30 дней: ${formatNumber(data.companies.new30d)}`}
            title="Показывает количество всех компаний в системе."
          />
          <Metric
            label="Новые за 7 дней"
            value={formatNumber(data.companies.new7d)}
            hint="Созданы за последнюю неделю"
            title="Показывает количество компаний, созданных за последние семь дней."
          />
          <Metric
            label="Активны за 7 дней"
            value={formatNumber(data.companies.active7d)}
            hint="Сделали хотя бы один звонок"
            title="Показывает компании, у которых был хотя бы один звонок за последние семь дней."
          />
          <Metric
            label="Запущенные обзвоны"
            value={formatNumber(data.campaignsRunning)}
            hint="Работают прямо сейчас"
            title="Показывает количество обзвонов, которые сейчас выполняются."
          />
        </div>
      </section>

      <section aria-labelledby="calls-title">
        <h2 id="calls-title" className={styles.sectionTitle}>
          Звонки
        </h2>
        <div className={common.metrics}>
          <Metric
            label="Звонков за период"
            value={formatNumber(data.calls.total)}
            hint={`Телефонных ${formatNumber(data.calls.phone)}, тестовых ${formatNumber(data.calls.test)}`}
            title="Показывает количество телефонных и тестовых звонков за выбранный период."
          />
          <Metric
            label="Минут разговора"
            value={formatDecimal(data.calls.minutes)}
            hint={`Агент говорил ${formatDecimal(data.calls.agentMinutes)} мин`}
            title="Показывает общую длительность звонков и время речи агента за выбранный период."
          />
          <Metric
            label="Доля ответов"
            value={formatPercent(data.calls.answerRate)}
            hint={`Средний звонок ${formatDurationSeconds(Math.round(data.calls.avgDurationSec))}`}
            title="Показывает долю телефонных звонков, в которых собеседник ответил."
          />
          <Metric
            label="Ошибки"
            value={formatNumber(data.calls.failed)}
            hint={`Сегодня ${formatNumber(data.calls.today)}, за 7 дней ${formatNumber(data.calls.last7d)}, за 30 дней ${formatNumber(data.calls.last30d)}`}
            title="Показывает количество звонков, завершившихся технической ошибкой."
          />
        </div>
      </section>

      <CallsChart points={data.daily.slice(-30)} />

      <section aria-labelledby="clients-title">
        <h2 id="clients-title" className={styles.sectionTitle}>
          Клиенты и расходы
        </h2>
        <div className={common.metrics}>
          <Metric
            label="Контакты"
            value={formatNumber(data.contactsTotal)}
            hint={`Горячих клиентов: ${formatNumber(data.hotClients)}`}
            title="Показывает количество контактов во всех клиентских базах."
          />
          <Metric
            label="Расход модели"
            value={costs.llmUsd === undefined ? '$—' : formatUsd(costs.llmUsd)}
            hint={`${formatNumber(data.costs.promptTokens)} входных токенов`}
            title="Показывает учтённую стоимость работы диалоговых моделей."
          />
          <Metric
            label="Синтез речи"
            value={
              <ElevenLabsCost
                credits={costs.ttsCredits}
                usd={costs.ttsUsd}
                rate={costs.elevenLabsUsdPer1kCredits}
              />
            }
            hint={costs.elevenLabsUsdPer1kCredits === undefined ? 'Тариф не передан API' : 'Кредиты ElevenLabs'}
            title="Показывает кредиты ElevenLabs и оценку их стоимости по тарифу API."
          />
          <Metric
            label="Всего за период"
            value={formatApproxUsd(costs.totalUsd)}
            hint="Диалоговая модель + синтез речи"
            title="Показывает полную оценку расходов без телефонии и распознавания речи."
          />
          <Metric
            label="Ответы модели"
            value={formatNumber(data.costs.completionTokens)}
            hint="Токены ответов · распознавание речи пока не считается"
            title="Показывает количество токенов, сгенерированных диалоговыми моделями."
          />
        </div>
      </section>
    </>
  )
}

interface MetricProps {
  label: string
  value: ReactNode
  hint: string
  title: string
}

function Metric({ label, value, hint, title }: MetricProps) {
  return (
    <article className={common.metric} title={title}>
      <span className={common.metricLabel}>{label}</span>
      <strong className={common.metricValue}>{value}</strong>
      <span className={common.metricHint}>{hint}</span>
    </article>
  )
}

function CallsChart({ points }: { points: AdminDailyPoint[] }) {
  if (points.length === 0) {
    return (
      <section className={common.panel}>
        <h2 className={styles.sectionTitle}>Звонки по дням</h2>
        <div className={styles.emptyChart}>За выбранный период звонков пока не было.</div>
      </section>
    )
  }

  const width = 720
  const height = 220
  const padX = 28
  const padY = 24
  const max = Math.max(1, ...points.map((point) => point.calls))
  const x = (index: number) =>
    padX + (index / Math.max(1, points.length - 1)) * (width - padX * 2)
  const y = (value: number) => height - padY - (value / max) * (height - padY * 2)
  const callsLine = points.map((point, index) => `${x(index)},${y(point.calls)}`).join(' ')
  const failedLine = points.map((point, index) => `${x(index)},${y(point.failed)}`).join(' ')
  const labelIndexes = [0, Math.floor(points.length / 2), points.length - 1].filter(
    (index, position, all) => all.indexOf(index) === position,
  )
  const labels = labelIndexes
    .map((index) => points[index])
    .filter((point): point is AdminDailyPoint => Boolean(point))

  return (
    <section className={`${common.panel} ${styles.chartPanel}`}>
      <div className={styles.chartHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Звонки по дням</h2>
          <p className={common.hint}>Последние 30 доступных дней</p>
        </div>
        <div className={styles.legend} aria-label="Обозначения графика">
          <span><i className={styles.callsDot} />Все звонки</span>
          <span><i className={styles.failedDot} />Ошибки</span>
        </div>
      </div>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Количество звонков и ошибок по дням"
      >
        <title>Количество звонков и ошибок по дням за последние 30 доступных дней</title>
        {[0.25, 0.5, 0.75, 1].map((share) => (
          <line
            key={share}
            className={styles.gridLine}
            x1={padX}
            x2={width - padX}
            y1={y(max * share)}
            y2={y(max * share)}
          />
        ))}
        <polyline className={styles.callsLine} points={callsLine} />
        <polyline className={styles.failedLine} points={failedLine} />
      </svg>
      <div className={styles.chartLabels}>
        {labels.map((point) => (
          <span key={point.date}>{formatDay(point.date)}</span>
        ))}
      </div>
    </section>
  )
}
